/**
 * livepoll.ts — WHEN to poll `/api/live`, as a pure function.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Andrei, 2026-08-05: "the app seems to be slow and not responsive". He browses through a VPN whose
 * exit node is in FRANCE, so every request from his laptop pays a transatlantic round trip on top of
 * the server's own time. Measured from us-east-1 the site answers with 118 ms TTFB (dns 7 / connect
 * 9 / tls 18); through Paris each call costs roughly +300 ms more. Production CloudWatch ROUTE lines
 * over a 2 h window, ONE browser:
 *
 *   | route    | calls | avg     |
 *   |----------|-------|---------|
 *   | account  |   200 | 1383 ms |
 *   | overlays |   392 | 1009 ms |
 *   | live     |   594 |  676 ms |   <- the biggest COUNT
 *
 * 1566 calls in 120 minutes. The server is not the problem; the CALL COUNT is. So the fix is fewer
 * calls, and the cheapest ones to delete are the polls nobody can see the result of.
 *
 * TWO RULES, both of which used to be missing.
 *
 * 1. THE POLL INTERVAL SCALES WITH THE TIMEFRAME. `LIVE_POLL_MS` was a flat 5000 for every pane, so
 *    the 1W pane — whose bar closes once a WEEK — was asked for a fresh candle every 10 s by the
 *    round-robin. That is 6 requests per minute spent to learn nothing: a weekly bar cannot move
 *    perceptibly between two ticks ten seconds apart. `livePollMs` derives the cadence from the bar
 *    period instead.
 *
 * 2. A HIDDEN TAB DOES NOT POLL. `document.visibilityState` is native, free, and was not consulted.
 *    Chrome already throttles background `setInterval` to about once a minute, which is why the
 *    measured 594 is below the 24/min the timers ask for — but "about once a minute" is not zero,
 *    and every one of those still costs a Paris round trip to repaint pixels nobody is looking at.
 *
 * NO CACHE, NO SERVICE WORKER, NO STORE. This file decides WHETHER to ask; it never remembers an
 * answer. A second copy of data that already has a source of truth is how a stale chart happens.
 *
 * WHY A SEPARATE MODULE AND NOT AN `if` INSIDE `startLive`
 * -------------------------------------------------------
 * Same reason as `barclose.ts`: a decision buried in a `setInterval` callback in `main.ts` is
 * structurally unreachable by vitest, and gate 5 exists because a frontend state bug shipped green
 * through the other four gates. The scheduling rule is pure, so it is tested rather than trusted.
 */

/**
 * Length of one bar, in seconds — or `null` if the string is not a timeframe.
 *
 * DERIVED BY PARSING, NOT A HAND-WRITTEN MAP. A `Record<Timeframe, number>` is the recurring defect
 * in this repo: `KNOWN_OVERLAYS`, `params_for` and the shared-knob id list all drifted from the thing
 * they mirrored, and each drift shipped as a control that renders and does nothing. `ALL_TIMEFRAMES`
 * is `['1M','1W','1D','4h','1h','15m','5m','1m']`; a `30m` or `2h` added there needs no edit here.
 * (`5m` was added on 2026-08-24, card t_e7baebb0, and this file needed no change beyond re-quoting
 * the list — which is the property the sentence above claims, so the quotation is kept true.)
 *
 * CASE-SENSITIVE, and that is load-bearing: `1M` is a MONTH and `1m` is a MINUTE — a factor of
 * 43,200. Lowercasing the unit would give the monthly pane a 5-second poll.
 *
 * A month is 30 days and a year is not modelled. This is a poll cadence, not a calendar: the value
 * is only ever compared against a clamp, and 30 vs 31 days cannot change which side of it lands.
 */
export function barSeconds(tf: string): number | null {
  const m = /^(\d+)([mhDWM])$/.exec(tf);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit: Record<string, number> = {
    m: 60,
    h: 3600,
    D: 86400,
    W: 604800,
    M: 2592000,
  };
  return n * unit[m[2]];
}

/**
 * The FLOOR on any poll, in ms, and the tick the single timer runs at.
 *
 * 5000, unchanged from the old flat `LIVE_POLL_MS`, because the 15m pane is the one Andrei actually
 * trades and CLAUDE.md §4 bar-close discipline forbids a poll slower than the bar. `1m` and `15m`
 * both land here, so the pane he watches is byte-for-byte as fresh as before this change.
 */
export const LIVE_TICK_MS = 5000;

/**
 * The CEILING, in ms. 60 s.
 *
 * Every timeframe at or above 4h clamps to it. That is deliberately conservative rather than clever:
 * 60 s is ~1/240th of a 4h bar and ~1/10000th of a weekly one, so the candle is never visibly stale,
 * and it keeps the HTF panes at 1 request/min each instead of 6.
 */
export const LIVE_POLL_MAX_MS = 60000;

/**
 * How often this timeframe's forming candle is worth re-asking for.
 *
 * `barSeconds / 180`, clamped. The divisor is anchored on the pane that matters: a 15m bar is 900 s
 * and 900/180 = 5 s exactly, so the execution pane's cadence is DERIVED rather than special-cased.
 * Everything slower than 4h hits the ceiling.
 *
 *   | tf  | bar     | poll  |
 *   |-----|---------|-------|
 *   | 1m  | 60 s    |  5 s  (floor)
 *   | 15m | 900 s   |  5 s
 *   | 1h  | 3600 s  | 20 s
 *   | 4h  | 14400 s | 60 s  (ceiling)
 *   | 1D  | 1 day   | 60 s  (ceiling)
 *   | 1W  | 1 week  | 60 s  (ceiling)
 *
 * An UNRECOGNISED timeframe gets the FLOOR, not the ceiling. A tf this function cannot parse is a
 * timeframe someone added without reading this file, and polling it too often is a wasted request
 * while polling it too rarely is a chart showing a stale price — the second is the worse bug.
 */
export function livePollMs(tf: string): number {
  const bar = barSeconds(tf);
  if (bar === null) return LIVE_TICK_MS;
  return Math.min(LIVE_POLL_MAX_MS, Math.max(LIVE_TICK_MS, Math.round((bar / 180) * 1000)));
}

/** A pane, as this scheduler needs to see it. */
export type PollTarget = { id: string; tf: string };

/**
 * Which panes should be polled at `now`.
 *
 * `hidden` is `document.visibilityState === 'hidden'`, passed in rather than read here so the rule is
 * testable without a DOM and so there is exactly one place that reads the browser.
 *
 * A pane with no entry in `lastPolledAt` is ALWAYS due — that is the first tick after boot, an asset
 * change, or a timeframe change, and each of those genuinely needs a candle now.
 *
 * RETURNS THE PANES, and the caller records the time it actually polled them. The decision does not
 * mutate, so a failed fetch can choose whether to count as a poll.
 */
export function panesDue(
  panes: readonly PollTarget[],
  lastPolledAt: ReadonlyMap<string, number>,
  now: number,
  hidden: boolean,
): PollTarget[] {
  // THE VISIBILITY GATE. Returned as "no panes", not as a shortened list: a hidden tab must issue
  // ZERO requests, including for the execution pane. Nobody is reading a chart they cannot see, and
  // `startLive`/`visibilitychange` re-polls on the way back so it is never stale on return.
  if (hidden) return [];
  return panes.filter((p) => {
    const last = lastPolledAt.get(p.id);
    if (last === undefined) return true;
    return now - last >= livePollMs(p.tf);
  });
}
